import {Product} from '@groupthought/method-ui/types';
import Alpine, {AlpineComponent} from 'alpinejs';

export type ProductVariantCardData = AlpineComponent<{
  productId: number;
  variantId: number | null;

  get product(): Product | null;

  /**
    The URL of the product card.
  */
  get productCardUrl(): string | undefined;

  /**
    A variant may either be selected by the user explicitly (e.g. by clicking a swatch) or programatically
    when the user hovers/focuses quick action buttons when a variant is not selected.
    The variant is considered 'selected' either way however when the user leaves the card, the programatically selected variant is cleared.
  */
  userSelectedVariantId: number | null;
  autoSelectedVariantId: number | null;
  get hasSelectedVariant(): boolean;

  /**
    Clears the selected variant.
  */
  clearVariant(): void;
}>;

export type ProductSiblingCardData = AlpineComponent<{
  productId: number;
  variantId: number | null;

  get product(): Product | null;

  /**
    The URL of the product card.
  */
  get productCardUrl(): string | undefined;

  /**
    The display product is the product for which we should show media, badges, prices, etc.
    It is the selected product.
  */
  get displayProductId(): number | null;
  get hasDisplayProduct(): boolean;
  get displayProduct(): Product | null;
}>;

// Alpine Data for variant-based product cards
function productCardVariantData(productId: number, initialVariantId: number | null): ProductVariantCardData {
  return {
    productId,
    userSelectedVariantId: null,
    autoSelectedVariantId: null,

    get variantId() {
      return this.autoSelectedVariantId ?? this.userSelectedVariantId ?? initialVariantId;
    },

    set variantId(variantId) {
      this.userSelectedVariantId = variantId;
    },

    get product() {
      return this.$cache.products.get(this.productId);
    },

    get productCardUrl() {
      const url = this.product?.url;
      if (!url || !this.variantId) return url;

      // In filtered collections product.url already carries a query string (tracking params and
      // the filter-matched variant), so the variant param must be replaced, not appended
      const [path, query] = url.split('?');
      const params = new URLSearchParams(query);
      params.set('variant', String(this.variantId));
      return `${path}?${params.toString()}`;
    },

    get hasSelectedVariant() {
      return this.variantId !== null;
    },

    clearVariant() {
      this.userSelectedVariantId = null;
    },
  };
}

// Alpine Data for sibling-based product cards
function productCardSiblingData(productId: number): ProductSiblingCardData {
  return {
    productId,

    get variantId() {
      return this.product?.selected_or_first_available_variant?.id ?? null;
    },

    get product() {
      return this.$cache.products.get(this.productId);
    },

    get productCardUrl() {
      return this.displayProduct?.url;
    },

    get displayProductId() {
      return this.productId;
    },

    get displayProduct() {
      return this.$cache.products.get(this.productId) || null;
    },

    get hasDisplayProduct() {
      return this.displayProductId !== null;
    },
  };
}

Alpine.data('productCardVariantData', productCardVariantData as any);
Alpine.data('productCardSiblingData', productCardSiblingData as any);
